import React, { Component } from 'react'
import { render } from 'react-dom'
import  '../../App.css'

import { InlineMath, BlockMath } from 'react-katex';

import {
  Header,
} from 'semantic-ui-react'

import 'katex/dist/katex.min.css';

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dark, darcula } from 'react-syntax-highlighter/dist/esm/styles/prism';

class Chapter104 extends Component {

  render() {
    return (
      <div className="base">

        <Header as='h1'>
        1-4. ポアソン分布と二項分布のまとめ
        </Header>

1.4 では「ポアソン分布と二項分布のまとめ」と主題でまとめていますが、重要な点は繰り返しになりますが以下の通りです。

<ul classNmae="ul_01">
  <li className="li_01">消費者があるカテゴリーを選ぶ場合もあるブランドを選ぶ場合もその仕組みは同じである。すなわち、消費者がどのカテゴリーを選ぶかという問題と、どのブランドを選ぶかという問題は同じ確率分布を用いて解く事が出来る。</li>
  <li className="li_01">消費者個人の購買の分布は「ポアソン分布」している。</li>
  <li className="li_01">上記2点の結果として、ある期間における消費者全体の購入回数の分布は「負の二項分布」に従う </li>
</ul>

　ここで述べていることは、教科書と原因と結果の因果関係が異なります。教科書では、ポアソン分布と負の二項分布の結果からガンマ分布を導かれると書かれていますが、同じページの下の部分では、ポアソン分布とガンマ分布から負の二項分布が導かれると書かれているので、ここではそちらの理解の立場を取ります。 <br/>

        <Header as='h2'>
          <InlineMath>{`\\displaystyle Gamma \\left(K,\\frac{M}{K} \\right) `}</InlineMath>の表記
        </Header>

　一般的にガンマ分布を表す数式は、形状を決定するパラメタ <InlineMath>{`\\displaystyle \\alpha `}</InlineMath>, <InlineMath>{`\\displaystyle \\beta `}</InlineMath>を用いて、

 <BlockMath>{`
 f\\left(x|\\alpha, \\beta \\right) =\\frac{\\beta^{\\alpha}x^{\\alpha - 1}e^{-\\beta x}}{\\Gamma\\left(\\alpha \\right)} \\,\\,\\,\\,\\cdot\\cdot\\cdot\\cdot\\,\\,\\,\\,\\left(1\\right) 
`}</BlockMath>

と表現されます。また、<InlineMath>{`\\displaystyle \\beta =\\frac{1}{\\theta} `}</InlineMath>として、

<BlockMath>{`
f\\left(x|\\alpha, \\theta \\right) =\\frac{x^{\\alpha - 1}e^{-\\frac{x}{\\theta}}}{\\Gamma\\left(\\alpha \\right)\\theta^{\\alpha}} \\,\\,\\,\\,\\cdot\\cdot\\cdot\\cdot\\,\\,\\,\\,\\left(2\\right) 
`}</BlockMath>

<a href="https://en.wikipedia.org/wiki/Gamma_distribution" target="_blank">wikipedia</a>でもこの二通りの数式で表記されています。ここで、(1)と(2)における確率分布の平均と標準偏差は以下の通りです。


<table border="1" cellpadding="15" className="table_base">
  <tr>
    <th className="th_01">ガンマ分布</th>
    <th className="th_01"><InlineMath>{` \\displaystyle E[x] `}</InlineMath></th>
    <th className="th_01"><InlineMath>{` \\displaystyle V[x] `}</InlineMath></th>
  </tr>
  <tr>
    <td className="th_01"><InlineMath>{` \\displaystyle \\frac{\\beta^{\\alpha}x^{\\alpha - 1}e^{-\\beta x}}{\\Gamma\\left(\\alpha \\right)}  `}</InlineMath></td>
    <td className="th_01"><InlineMath>{` \\displaystyle \\frac{\\alpha}{\\beta} `}</InlineMath></td>
    <td className="th_01"><InlineMath>{` \\displaystyle \\frac{\\alpha}{\\beta^2} `}</InlineMath></td>
  </tr>
  <tr>
    <td className="th_01"><InlineMath>{` \\displaystyle \\frac{x^{\\alpha - 1}e^{-\\frac{x}{\\theta}}}{\\Gamma\\left(\\alpha \\right)\\theta^{\\alpha}}  `}</InlineMath></td>
    <td className="th_01"><InlineMath>{` \\displaystyle \\alpha\\theta `}</InlineMath></td>
    <td className="th_01"><InlineMath>{` \\displaystyle \\alpha\\theta^2 `}</InlineMath></td>
  </tr>
</table>


本書では、<InlineMath>{`\\displaystyle Gamma \\left(K,\\frac{M}{K} \\right)`}</InlineMath>がどちらの表記を用いているか明示されていませんが、<InlineMath>{`\\displaystyle Gamma \\left(1,5 \\right)`}</InlineMath>、<InlineMath>{`\\displaystyle Gamma \\left(3,\\frac{5}{3} \\right)`}</InlineMath>、<InlineMath>{`\\displaystyle Gamma \\left(15,\\frac{5}{15} \\right)`}</InlineMath>の平均値はすべて5と述べているので、(2)の表記を用いていると思われます。

        <Header as='h2'>
          python code
        </Header>

　ガンマ分布を記述するpythonのコードです。moduleとしてscipy、numpy、matplotlibなど機械学習ではおなじみのライブラリを用いてます。

        <SyntaxHighlighter language="python" style={darcula}>
{`
import numpy as np
import scipy
from scipy.stats import gamma
import matplotlib.pyplot as plt

def plog_gamma():

  x = np.linspace(0,50,1000)

  a = 1.0 
  b = 5.0
  mean, var, skew, kurt = gamma.stats(a, scale=b, moments='mvsk')
  y1 = gamma.pdf(x, a, scale=b)
  print(mean)

  a = 3.0
  b = 5.0/3.0
  mean, var, skew, kurt = gamma.stats(a, scale=b, moments='mvsk')
  print(mean)
  y2 = gamma.pdf(x, a, scale=b)

  a = 15.0
  b = 1.0/3.0
  mean, var, skew, kurt = gamma.stats(a, scale=b, moments='mvsk')
  print(mean)
  y3 = gamma.pdf(x, a, scale=b)

  plt.ylim([0,0.30])
  plt.xlim([0,10])

  plt.plot(x, y1, x, y2, x, y3)

  plt.savefig('gamma.png') 

if __name__ == "__main__":
  plog_gamma()
`}

        </SyntaxHighlighter>


        <Header as='h2'>
          ガンマ分布のpython code
        </Header>

　上記のコードの実行結果です。

- <InlineMath>{`\\displaystyle \\left(K,\\frac{M}{K} \\right) = \\left(1,5 \\right)`}</InlineMath> ,<InlineMath>{` \\left(3,\\displaystyle \\frac{5}{3} \\right)`}</InlineMath>, <InlineMath>{`\\left(15,\\displaystyle \\frac{5}{15} \\right)`}</InlineMath> の三通りについてプロットしています。教科書のP61の図2-2と同じようなグラフが得られています

        <img style={{"width":"100%", "margin":"0 auto"}} src={`${process.env.PUBLIC_URL}/images/gamma.png`} alt="gamma" />

      </div>
    )   
  }
}

export default Chapter104;
